import { useAcceptUi } from '@/hooks/useAcceptUi';
import type { AcceptUiPaymentResult } from '@/types/acceptUi';
import { Button, ButtonProps } from '@mantine/core';
import { useEffect } from 'react';

interface BillingAddressOptions {
  show: boolean;
  required: boolean;
}

interface AcceptUiButtonProps extends Omit<ButtonProps, 'onClick' | 'ref'> {
  apiLoginId: string;
  publicClientKey: string;
  environment: 'sandbox' | 'production';
  onPayment: (result: AcceptUiPaymentResult) => void;
  billingAddressOptions?: BillingAddressOptions;
  formHeaderText?: string;
  formButtonText?: string;
}

/**
 * Mantine `<Button>` wrapper that binds AcceptUI's hosted-iframe modal.
 *
 * On click, AcceptUI's script (loaded by `useAcceptUi`) opens its iframe modal
 * for card capture. On submit, AcceptUI calls `window.guardDogAcceptUiHandler` with the
 * tokenized result, which the hook surfaces via `paymentResult` state. This
 * component watches that state and forwards to the parent via `onPayment`.
 *
 * The `className="AcceptUI"` is required — it's the selector AcceptUI scans for
 * to bind the click handler. Mantine appends its own classes alongside.
 */
export const AcceptUiButton = ({
  apiLoginId,
  publicClientKey,
  environment,
  onPayment,
  billingAddressOptions = { show: true, required: true },
  formHeaderText = 'Card Information',
  formButtonText = 'Pay',
  disabled,
  children,
  ...buttonProps
}: AcceptUiButtonProps) => {
  const { buttonRef, scriptLoaded, scriptError, paymentResult, reset } = useAcceptUi({ environment });

  useEffect(() => {
    if (paymentResult) {
      onPayment(paymentResult);
      reset();
    }
  }, [paymentResult, onPayment, reset]);

  return (
    <Button
      ref={buttonRef}
      className="AcceptUI"
      data-apiLoginID={apiLoginId}
      data-clientKey={publicClientKey}
      data-billingAddressOptions={JSON.stringify(billingAddressOptions)}
      data-paymentOptions={JSON.stringify({ showCreditCard: true, showBankAccount: false })}
      data-acceptUIFormBtnTxt={formButtonText}
      data-acceptUIFormHeaderTxt={formHeaderText}
      data-responseHandler="guardDogAcceptUiHandler"
      disabled={!scriptLoaded || !!scriptError || disabled}
      {...buttonProps}
    >
      {children ?? formButtonText}
    </Button>
  );
};
